import { ListButton } from "@refinedev/antd";
import {
  Select,
  Spin,
  Switch,
  Tabs,
  Typography,
  message,
  Card,
  Collapse,
  Modal,
  InputNumber,
} from "antd";
import { useGetIdentity, useNavigation, useTranslate } from "@refinedev/core";
import {
  CheckOutlined,
  MailOutlined,
  WhatsAppOutlined,
  CopyOutlined,
  ContactsOutlined,
  DeleteOutlined,
  ReadOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  ShopOutlined,
  ApiOutlined,
  AppstoreOutlined,
  BlockOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
  ScissorOutlined,
} from "@ant-design/icons";
import { Flex, Button, theme } from "antd";
import { useContext, useEffect, useState } from "react";
import { ChannelAccessPermissions } from "@/components/channel-access-permissions";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { axiosInstance, marginUnitLabel } from "@/utilities";
import {
  API_URL_CONTACTS,
  API_URL_PERMISSIONS,
  API_URL_STORE_LINKED_SELLER,
  API_URL_USERS,
} from "@/services/XCONS_ENV";
import { useLocation, useParams } from "react-router-dom";
import { LateralModal } from "@/components/modals/LateralModal";
import { SellersUserPermissionsCopy } from "./userPermissionsCopy";
import { BoxCloseIcon, KeyRound, PresupuestoIcon } from "@/components";
import { requiredCodes } from "@/services/STORE_SELLERS_CONFIG";
import { User } from "@/types/schema/schema.types";
import { NIVEL, PARA_TODO } from "@/utilities/niveles-de-permiso";

const PERMISSION_OPTIONS = [
  { label: NIVEL.admin, value: "admin" },
  { label: NIVEL.editor, value: "editor" },
  { label: NIVEL.viewer, value: "viewer" },
  { label: NIVEL.by_relation, value: "by_relation" },
  { label: NIVEL.no_access, value: "no_access" },
];

const VALUE_LABEL: Record<string, string> = NIVEL;

type PermissionItem = {
  code: string;
  value: string;
  description?: string;
};

type ConfigItem = {
  code: string;
  value: string;
  description?: string;
};

const { Option } = Select;
const { Title, Text } = Typography;
const { Panel } = Collapse;

export const SellersUserPermissionsPanel = ({
  typeIdentifier = "seller",
}: {
  typeIdentifier?: string;
}) => {
  const [structureLoading, setStructureLoading] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [isUserActive, setIsUserActive] = useState<boolean>(true);
  const [storeName, setStoreName] = useState<string>("");
  const [storesPermissions, setStoresPermissions] = useState<any[]>([]);
  const [storeSellerPermissions, setStoreSellerPermissions] = useState<Record<string, { permissions: PermissionItem[]; configs: ConfigItem[] }>>({});
  const [activeStoreTab, setActiveStoreTab] = useState<string>("");
  const [userData, setUserData] = useState<any>();

  const t = useTranslate();
  const { token } = theme.useToken();
  const { goBack } = useNavigation();

  const location = useLocation();
  const { id } = useParams();
  const record = location.state?.record.user;

  const { smartMenuName } = useContext(SmartMenuContext);

  const storeIdentifier = encodeURIComponent(
    smartMenuName?.replace(/\"/g, "").toLowerCase(),
  );

  const getUserData = async () => {
    try {
      const res = await axiosInstance.get(`${API_URL_USERS}/${id}`);
      const data = res.data;
      setUserData(data);
    } catch (error) {
      console.error(error);
      handleBack();
    }
  };

  const handleBack = () => {
    goBack();
  };

  const humanizeValue = (val?: string | number | null) => {
    if (val === null || val === undefined) return "-";
    if (typeof val === "number") return String(val);
    return VALUE_LABEL[val] ?? String(val);
  };

  const pickValue = (item?: { value?: any; defaultValue?: any } | null) =>
    item?.value ?? item?.defaultValue;

  const getPermissions = async () => {
    setStructureLoading(true);

    const userID = record?.userId || id;

    try {
      const [res1, res2] = await Promise.allSettled([
        axiosInstance.get(
          `${API_URL_PERMISSIONS}/store/${storeIdentifier}/users/${userID}`,
        ),
        axiosInstance.get(
          `${API_URL_PERMISSIONS}/seller/${storeIdentifier}/users/${userID}`,
        ),
      ]);

      const data1 = res1.status === "fulfilled" ? res1.value.data : null;
      const data2 = res2.status === "fulfilled" ? res2.value.data : null;

      const mergedPermissions = [
        ...(data1?.permissions || []),
        ...(data2?.permissions || []),
      ];

      const mergedConfigs = [
        ...(data1?.configs || []),
        ...(data2?.configs || []),
      ];

      const dedupedPermissions = Object.values(
        Object.fromEntries(mergedPermissions.map((p) => [p.code, p])),
      );

      const dedupedConfigs = Object.values(
        Object.fromEntries(mergedConfigs.map((c) => [c.code, c])),
      );

      setIsUserActive(data2?.isActive ?? false);
      setPermissions(dedupedPermissions as PermissionItem[]);
      setConfigs(dedupedConfigs as ConfigItem[]);
      setStoreName(`${data1?.identifier ?? ""}`);
    } catch (error: any) {
      console.error("Error inesperado:", error);
      message.error("Ocurrió un error al cargar los permisos");
    } finally {
      setStructureLoading(false);
    }
  };

  const getStores = async () => {
    setStructureLoading(true);
    try {
      const res = await axiosInstance.get(
        `${API_URL_STORE_LINKED_SELLER}/${storeIdentifier}/stores`,
        { params: { "filter.storeLinkedSellers.active": "1" } },
      );

      const data = res.data.data;
      const linked = (data || []).filter((store: any) => {
        const link = Array.isArray(store.storeLinkedSellers)
          ? store.storeLinkedSellers[0]
          : store.storeLinkedSellers;
        return link && link.active;
      });
      setStoresPermissions(linked);

      if (linked.length > 0) {
        setActiveStoreTab(linked[0].storeCode);
      }

      // Fetch store_seller permissions for each linked store
      const permsMap: Record<string, { permissions: PermissionItem[]; configs: ConfigItem[] }> = {};
      await Promise.all(
        linked.map(async (store: any) => {
          try {
            const permRes = await axiosInstance.get(
              `${API_URL_PERMISSIONS}/store_seller/${store.storeCode}/users/${storeIdentifier}`,
            );
            permsMap[store.storeCode] = {
              permissions: permRes.data?.permissions || [],
              configs: permRes.data?.configs || [],
            };
          } catch {
            permsMap[store.storeCode] = { permissions: [], configs: [] };
          }
        }),
      );
      setStoreSellerPermissions(permsMap);
    } catch (error) {
      console.error("Error al obtener stores:", error);
    } finally {
      setStructureLoading(false);
    }
  };

  useEffect(() => {
    if (!record?.userId) {
      getUserData();
    }
    getPermissions();
    getStores();
  }, []);

  const getPermission = (code: string) =>
    permissions.find((p) => p.code === code);

  const getConfig = (code: string) => configs.find((c) => c.code === code);

  const renderPermissionValue = (item: any, isConfig = false) => {
    if (!item) return null;

    if (item.code === "quote_max_manual_discount") {
      return <Text className="text-permission">{pickValue(item)}</Text>;
    }

    return (
      <Text className="text-permission" style={{ textTransform: "capitalize" }}>
        {humanizeValue(pickValue(item))}
      </Text>
    );
  };

  const renderSelect = (item: any, isConfig = false) => {
    if (!item) return null;

    const setValue = (value: string) => {
      if (isConfig) {
        setConfigs((prev: any) =>
          prev.map((c: any) => (c.code === item.code ? { ...c, value } : c)),
        );
      } else {
        setPermissions((prev: any) =>
          prev.map((p: any) => (p.code === item.code ? { ...p, value } : p)),
        );
      }
    };

    // El control lo decide el API, no una lista de códigos acá: `options` viene en la
    // respuesta y un permiso con exactamente dos valores es binario (los de seller lo
    // son). Así el front no se desincroniza cuando cambian los contributors.
    if (item.options?.length === 2) {
      const [granted, denied] = item.options;

      return (
        <Switch
          checked={item.value === granted}
          onChange={(checked) => setValue(checked ? granted : denied)}
        />
      );
    }

    return (
      <Select
        options={PERMISSION_OPTIONS}
        value={item.value}
        onChange={setValue}
        style={{ minWidth: 180 }}
      />
    );
  };

  const updateGroupValues = (
    permissionCodes: string[],
    configCodes: string[],
    value: string,
  ) => {
    setPermissions((prev) =>
      prev.map((p) => (permissionCodes.includes(p.code) ? { ...p, value } : p)),
    );
    setConfigs((prev) =>
      prev.map((c) =>
        configCodes.includes(c.code) && c.code !== "quote_max_manual_discount"
          ? { ...c, value }
          : c,
      ),
    );
  };

  const handleConfigChange = (code: string, value: string | number | null) => {
    setConfigs((prevConfigs: any) =>
      prevConfigs.map((config: any) =>
        config.code === code ? { ...config, value: value } : config,
      ),
    );
  };

  const PERMISSION_GROUPS = [
    {
      key: "contacts",
      icon: <ContactsOutlined />,
      title: t("contacts.title.contacts"),
      subtitle: t("label.instruction.permissionscontact"),
      permissionCodes: [],
      configCodes: ["contact_conversation", "contact_quote"],
    },
    {
      key: "contacts_groups",
      icon: <TeamOutlined />,
      title: t("label.contactsgroups"),
      subtitle: t("label.permissionscontact.group"),
      permissionCodes: ["contact_group"],
      configCodes: [],
      noButton: true,
      singleHeader: true,
    },
    {
      key: "quotes",
      icon: <PresupuestoIcon />,
      title: t("menu.label.quotes"),
      subtitle: t("label.instruction.permissionsquotes"),
      permissionCodes: [],
      configCodes: [
        "quote_participation",
        "quote_max_manual_discount",
        "quote_access_margins",
        "quote_commission_discount_level",
      ],
    },
    {
      key: "orders",
      icon: <BoxCloseIcon />,
      title: t("menu.label.orders"),
      subtitle: t("label.instruction.orders"),
      permissionCodes: ["order_payment", "order_shipment"],
      configCodes: [],
    },
    {
      // Lo que el store le dio a este seller sobre su árbol de categorías (xcons-docs#445),
      // heredado como el resto: se ve y no se cambia. Lo decide el store, en el vínculo.
      key: "categorias",
      icon: <AppstoreOutlined />,
      title: t("label.permissions.categorias"),
      subtitle: t(
        "label.instruction.categorias",
        "Qué pueden hacer tus operadores con el árbol de categorías de este store.",
      ),
      permissionCodes: ["categorias"],
      configCodes: [],
      noButton: true,
      singleHeader: true,
    },
    {
      key: "linked_products",
      icon: <ApiOutlined />,
      title: t("smartMenu.label.linked"),
      permissionCodes: ["linked_products"],
      configCodes: [],
      individually: true,
    },
    {
      key: "linked_store",
      icon: <ShopOutlined />,
      title: t("contacts.groupsTable.linkedStore"),
      permissionCodes: ["store_linked_seller"],
      configCodes: [],
      individually: true,
    },
    {
      key: "seller_permission",
      icon: <KeyRound />,
      title: t("create.seller.labelSeller"),
      permissionCodes: ["seller_permission"],
      configCodes: [],
      individually: true,
    },
    {
      key: "seller_config",
      icon: <SettingOutlined />,
      title: t("permissions.seller.config"),
      permissionCodes: ["seller_config"],
      configCodes: [],
      individually: true,
    },
    {
      // Apps → Optimizer (xcons-docs#524): aparte de `seller_config` a propósito, para poder
      // delegar la máquina sin delegar los datos de la empresa.
      key: "optimizer_config",
      icon: <ScissorOutlined />,
      title: t("permissions.seller.optimizer", "Optimizer"),
      permissionCodes: ["optimizer_config"],
      configCodes: [],
      individually: true,
    },
  ];

  const individualPermissions = PERMISSION_GROUPS.filter((g) => g.individually);
  const groupedPermissions = PERMISSION_GROUPS.filter((g) => !g.individually);

  const LABELS_MAP: Record<string, React.ReactNode> = {
    contact_conversation: (
      <Text>
        {t("label.permissionscontact.conversation.part1")}
        <strong>{t("label.permissionscontact.conversation.part2")}</strong>
        {t("label.permissionscontact.conversation.part3")}
        <strong>{t("label.permissionscontact.conversation.part4")}</strong>
        {t("label.permissionscontact.conversation.part5")}
      </Text>
    ),
    contact_quote: <Text>{t("label.permissionscontact.quote")}</Text>,
    contact_group: <Text>{t("label.permissionscontact.group")}</Text>,
    quote_participation: <Text>{t("label.permissionsquotes")}</Text>,
    quote_max_manual_discount: <Text>{t("label.maximumdiscount")}</Text>,
    quote_access_margins: <Text>{t("label.accessmargins")}</Text>,
    quote_commission_discount_level: (
      <Text>{t("label.commissionlevel")}</Text>
    ),
    order_payment: <Text>{t("label.permissionspaymenttransactions")}</Text>,
    order_shipment: <Text>{t("Label.deliveries")}</Text>,
    store_permission: <Text strong>{t("contacts.tag.storeusers")}</Text>,
    seller_permission: <Text strong>{t("contacts.tag.sellerusers")}</Text>,
    linked_products: <Text strong>{t("linkedProducts.title")}</Text>,
    store_linked_seller: (
      <Text strong>{t("contacts.groupsTable.linkedStore")}</Text>
    ),
    seller_config: <Text strong>{t("permissions.seller.config")}</Text>,
    optimizer_config: (
      <Text strong>{t("permissions.seller.optimizer", "Optimizer")}</Text>
    ),
  };

  const getPermissionLabel = (code: string) =>
    LABELS_MAP[code] ?? `Permission: ${code}`;

  const getConfigLabel = (code: string) =>
    LABELS_MAP[code] ?? `Config: ${code}`;

  const handleRemoveGroup = (
    permissionCodes: string[],
    configCodes: string[],
  ) => {
    updateGroupValues(permissionCodes, configCodes, "no_access");

    if (configCodes.includes("quote_max_manual_discount")) {
      handleConfigChange("quote_max_manual_discount", 0);
    }
  };

  const handleSavePermissions = async () => {
    setStructureLoading(true);
    const userID = record?.userId || id;
    try {
      const payload = {
        type: typeIdentifier,
        identifier: storeIdentifier,
        userId: userID,
        isActive: isUserActive,
        permissions: permissions
          .filter((p) => requiredCodes.includes(p.code))
          .map((p) => ({
            code: p.code,
            value: p.value,
          })),
        configs: [],
      };

      await axiosInstance.post(`${API_URL_PERMISSIONS}`, payload);

      message.success("Permissions saved successfully");
    } catch (error) {
      console.error("Error al obtener permisos:", error);
    } finally {
      setStructureLoading(false);
    }
  };

  const handleDelete = async () => {
    setStructureLoading(true);
    const userID = record?.userId || id;
    try {
      await axiosInstance.delete(
        `${API_URL_PERMISSIONS}/${typeIdentifier}/${storeIdentifier}/users/${userID}`,
      );

      message.success("Permissions deleted successfully");
      handleBack();
    } catch (error) {
      console.error("Error al obtener permisos:", error);
    } finally {
      setStructureLoading(false);
    }
  };

  const warningDelete = () => {
    Modal.confirm({
      title: t("storeoperator.message.title.removeoperator"),
      content: t("storeoperator.message.label.removeoperator"),
      onOk: () => {
        handleDelete();
      },
      width: 490,
      okText: t("storeperator.message.label.unlinked"),
      okButtonProps: {
        type: "primary",
      },
      icon: <ExclamationCircleOutlined />,
      okType: "danger",
      cancelButtonProps: {
        icon: <ArrowLeftOutlined />,
      },
      cancelText: t("buttons.back"),
      onCancel() {},
    });
  };

  const handleRemoveRelations = async () => {
    const userID = record?.userId || id;
    if (!userID || !storeIdentifier) return;
    try {
      const decodedSellerCode = decodeURIComponent(storeIdentifier);
      const { data } = await axiosInstance.delete(
        `${API_URL_CONTACTS}/relations/operator/${userID}/seller/${decodedSellerCode}`,
      );
      const deleted = data?.deleted ?? 0;
      message.success(
        t("storeoperator.message.relations.removed", "Relaciones eliminadas") +
          ` (${deleted})`,
      );
    } catch (error: any) {
      const apiMsg =
        error?.response?.data?.message || error?.response?.data?.error;
      message.error(
        Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg ||
          t(
            "storeoperator.message.relations.removeError",
            "Error al remover relaciones",
          ),
      );
    }
  };

  const confirmRemoveRelations = () => {
    Modal.confirm({
      title: t(
        "storeoperator.message.title.removerelations",
        "¿Remover todas las relaciones?",
      ),
      content: t(
        "storeoperator.message.title.removerelations.two",
        "El operador dejará de auto-asignarse a nuevas conversaciones y presupuestos de sus clientes actuales. Las participaciones en registros históricos se preservan. Esta acción no se puede deshacer.",
      ),
      onOk: () => {
        handleRemoveRelations();
      },
      width: 520,
      okText: t(
        "storeoperator.message.label.confirmremoverelations",
        "Remover relaciones",
      ),
      okType: "danger",
      okButtonProps: { type: "primary" },
      icon: <ExclamationCircleOutlined />,
      cancelButtonProps: { icon: <ArrowLeftOutlined /> },
      cancelText: t("buttons.back"),
      onCancel() {},
    });
  };

  return (
    <Spin spinning={structureLoading}>
      <Flex vertical>
        <Flex align="center" justify="space-between">
          <Flex align="center">
            <ListButton
              onClick={handleBack}
              hideText
              icon={<ArrowLeftOutlined />}
            />
            <Title style={{ marginBottom: 0, marginLeft: "10px" }} level={3}>
              {`${t("permissionsOf")} ${
                record?.firstname || userData?.firstname
              } ${record?.lastname || userData?.lastname}`}
            </Title>
          </Flex>

          <Flex align="center" gap={6}>
            <Text style={{ color: token.colorTextSecondary }}>FAQ</Text>
            <ReadOutlined style={{ color: token.colorTextSecondary }} />
          </Flex>
        </Flex>

        <Card
          style={{
            margin: "24px 0",
            border: `1px solid ${token.colorBorder}`,
            borderRadius: "8px",
          }}
          styles={{ body: { padding: "8px 12px" } }}
        >
          <Flex
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
          >
            <Flex vertical gap={8}>
              <Flex align="center" gap={8}>
                <MailOutlined />
                <Text>{record?.email || userData?.email || "-"}</Text>
              </Flex>
              <Flex align="center" gap={8}>
                <WhatsAppOutlined />
                <Text>{record?.whatsapp || userData?.whatsapp || "-"}</Text>
              </Flex>
              <Text style={{ fontSize: "12px" }}>
                {t("userPermissions.editProfileInfo")}
              </Text>
            </Flex>
            <Flex vertical gap={25} align="end">
              <Flex align="center">
                <Text style={{ marginRight: "8px" }}>
                  {isUserActive
                    ? t("filter.products.labelStatusActive")
                    : t("filter.products.labelStatusDesactive")}
                </Text>
                <Switch
                  checked={isUserActive}
                  onChange={setIsUserActive}
                  className={isUserActive ? "active-switch" : ""}
                />
              </Flex>
              <Flex gap="8px">
                <LateralModal
                  key="copy-permissions-modal"
                  buttonName={t("copyPermissions")}
                  title={t("copyPermissions")}
                  buttonType="default"
                  buttonShape="default"
                  maskClosable={false}
                  iconSvg={<CopyOutlined />}
                >
                  <SellersUserPermissionsCopy
                    t={t}
                    usersData={record || userData}
                    sellerCode={storeIdentifier}
                    permissions={permissions}
                    configs={configs}
                  />
                </LateralModal>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={confirmRemoveRelations}
                >
                  {t(
                    "storeoperator.button.removerelations",
                    "Remover relaciones",
                  )}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Card>

        <Flex vertical gap={0}>
          <Collapse defaultActiveKey={["permissions"]}>
            <Panel
              className="permissions-collapse"
              key="permissions"
              header={
                <Flex vertical>
                  <Text strong>{t("menu.label.salesManagement")}</Text>
                  <Text style={{ fontSize: 12 }}>
                    {t("permissions.inheritedFromStore")}
                  </Text>
                </Flex>
              }
            >
              {storesPermissions.length > 0 ? (
                <Tabs
                  activeKey={activeStoreTab}
                  onChange={setActiveStoreTab}
                  items={storesPermissions.map((store: any) => ({
                    key: store.storeCode,
                    label: (
                      <span>
                        <ShopOutlined /> {store.name}
                      </span>
                    ),
                    children: (() => {
                      const storePerms = storeSellerPermissions[store.storeCode];
                      const getStorePerm = (code: string) =>
                        storePerms?.permissions?.find((p: any) => p.code === code);
                      const getStoreConf = (code: string) =>
                        storePerms?.configs?.find((c: any) => c.code === code);

                      return (
                        <>
                          {groupedPermissions.map((group, index) => {
                            // Single-row group (e.g. Grupos de contactos): render
                            // header with icon + title + subtitle + value on the
                            // right, no body rows.
                            if ((group as any).singleHeader) {
                              const code = group.permissionCodes[0] ?? group.configCodes[0];
                              const isConfig = group.configCodes.length > 0;
                              const item = isConfig ? getStoreConf(code) : getStorePerm(code);
                              // Un API anterior a xcons-docs#445 no manda `categorias` para el par.
                              if (!item && group.key === "categorias") return null;
                              return (
                                <Card
                                  key={`${group.key}-${index}`}
                                  className={`permissions-card-section ${group.key === "contacts_groups" ? "contacts-groups-card" : ""}`}
                                >
                                  <Flex justify="space-between" align="center">
                                    <Flex vertical>
                                      <Text
                                        strong
                                        style={{ display: "flex", alignItems: "center", gap: 4 }}
                                      >
                                        {group.icon} {group.title}
                                      </Text>
                                      <Text style={{ fontSize: 12, marginLeft: 19 }}>
                                        {group.subtitle}
                                      </Text>
                                    </Flex>
                                    <Flex style={{ textTransform: "capitalize" }}>
                                      {renderPermissionValue(item, isConfig)}
                                    </Flex>
                                  </Flex>
                                </Card>
                              );
                            }
                            return (
                            <Card
                              key={`${group.key}-${index}`}
                              className={`permissions-card-section ${
                                group.key === "contacts_groups"
                                  ? "contacts-groups-card"
                                  : ""
                              }`}
                              title={
                                !group.noButton &&
                                (group.permissionCodes.length > 1 ||
                                  group.configCodes.length > 1) && (
                                  <Flex justify="space-between" align="center">
                                    <Flex vertical>
                                      <Text
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 4,
                                        }}
                                      >
                                        {group.icon} {group.title}
                                      </Text>
                                      <Text style={{ fontSize: "12px", marginLeft: 19 }}>
                                        {group.subtitle}
                                      </Text>
                                    </Flex>
                                    <Flex>
                                      <Text className="text-permission">
                                        {(() => {
                                          if (group.configCodes.length > 0) {
                                            const firstConf = getStoreConf(group.configCodes[0]);
                                            return `${humanizeValue(pickValue(firstConf))} ${PARA_TODO}`;
                                          }
                                          if (group.permissionCodes.length > 0) {
                                            const firstPerm = getStorePerm(group.permissionCodes[0]);
                                            return `${humanizeValue(pickValue(firstPerm))} ${PARA_TODO}`;
                                          }
                                          return "";
                                        })()}
                                      </Text>
                                    </Flex>
                                  </Flex>
                                )
                              }
                            >
                              {group.configCodes.map((code, i) => (
                                <Flex
                                  key={`${group.key}-${code}-${i}`}
                                  justify="space-between"
                                  align="center"
                                  style={{ marginBottom: 18 }}
                                >
                                  <Text>{getConfigLabel(code)}</Text>
                                  <Flex>
                                    {code === "quote_access_margins" ? (
                                      // 028 — la unidad del margen, no un toggle.
                                      // Texto y no control deshabilitado, igual que
                                      // la comisión de al lado. La normalización mapea
                                      // las filas legacy ('true' → Markup).
                                      <Text className="text-permission">
                                        {marginUnitLabel(
                                          getStoreConf(code)?.value,
                                        )}
                                      </Text>
                                    ) : code ===
                                      "quote_commission_discount_level" ? (
                                      <Text className="text-permission">
                                        {getStoreConf(code)?.value === "editor"
                                          ? t("label.commission.editor")
                                          : getStoreConf(code)?.value ===
                                              "viewer"
                                            ? t("label.commission.viewer")
                                            : t("label.commission.none")}
                                      </Text>
                                    ) : (
                                      renderPermissionValue(
                                        getStoreConf(code),
                                        true,
                                      )
                                    )}
                                  </Flex>
                                </Flex>
                              ))}
                              {group.permissionCodes.map((code, i) => {
                                // Orders sub-permissions render as read-only
                                // switches in this panel — matches the spec's
                                // UX (switches for Payment/Deliveries) but
                                // disabled since the seller-admin views the
                                // configuration inherited from the Store and
                                // cannot edit it from here.
                                const isOrdersSub =
                                  group.key === "orders" &&
                                  (code === "order_payment" ||
                                    code === "order_shipment");
                                if (isOrdersSub) {
                                  const item = getStorePerm(code);
                                  const value = pickValue(item);
                                  const checked = !!value && value !== "no_access";
                                  return (
                                    <Flex
                                      key={`${group.key}-${code}-${i}`}
                                      justify="space-between"
                                      align="center"
                                      style={{ marginBottom: 16 }}
                                    >
                                      <Text>{getPermissionLabel(code)}</Text>
                                      <Switch checked={checked} disabled />
                                    </Flex>
                                  );
                                }
                                return (
                                  <Flex
                                    key={`${group.key}-${code}-${i}`}
                                    justify="space-between"
                                    align="center"
                                    style={group.noButton ? {} : { marginBottom: 16 }}
                                  >
                                    <Text>{getPermissionLabel(code)}</Text>
                                    <Flex style={{ textTransform: "capitalize" }}>
                                      {renderPermissionValue(getStorePerm(code))}
                                    </Flex>
                                  </Flex>
                                );
                              })}
                            </Card>
                            );
                          })}

                          {/* Conversations (N2) — **read only**: el nivel es del seller como
                              entidad y el operador lo hereda (001, rev 4). Un tab por store
                              vinculado, así que cada uno muestra los canales de ESE store con el
                              nivel del seller ahí. Sin `persist()`: no hay nada que guardar. */}
                          <ChannelAccessPermissions
                            storeCode={store.storeCode}
                            subject={{ kind: "seller", sellerCode: decodeURIComponent(storeIdentifier) }}
                            editable={false}
                          />
                        </>
                      );
                    })(),
                  }))}
                />
              ) : (
                <Text style={{ color: token.colorTextSecondary }}>
                  {t("linkedsellers.noLinkedStores") || "No hay stores linkeados activos"}
                </Text>
              )}
            </Panel>
          </Collapse>

          <Collapse defaultActiveKey={["store"]}>
            <Panel
              className="permissions-collapse"
              key="store"
              header={
                <Text strong>
                  <span style={{ textTransform: "capitalize" }}>
                    {storeName}
                  </span>{" "}
                  Seller administration
                </Text>
              }
            >
              {individualPermissions.map((group, index) => (
                <Flex
                  key={`indiv-${group.key}-${index}`}
                  style={{ marginBottom: 16 }}
                >
                  {/* Permisos individuales */}
                  {group.permissionCodes.map((code, i) => {
                    return (
                      <Card
                        key={`perm-${group.key}-${code}-${i}`}
                        className="permissions-card-indivituallity"
                      >
                        <Flex justify="space-between" align="center">
                          <Flex align="center" gap={8}>
                            {group.icon}
                            <Text>{getPermissionLabel(code)}</Text>
                          </Flex>
                          {renderSelect(getPermission(code))}
                        </Flex>
                      </Card>
                    );
                  })}

                  {/* Configuraciones individuales */}
                  {group.configCodes.map((code, i) => (
                    <Card
                      key={`conf-${group.key}-${code}-${i}`}
                      className="permissions-card-indivituallity"
                    >
                      <Flex justify="space-between" align="center">
                        <Flex align="center" gap={8}>
                          {group.icon}
                          <Text>{getConfigLabel(code)}</Text>
                        </Flex>
                        {renderSelect(getConfig(code), true)}
                      </Flex>
                    </Card>
                  ))}
                </Flex>
              ))}
            </Panel>
          </Collapse>
        </Flex>

        <Flex justify="space-between" style={{ marginTop: 24 }}>
          <Button
            danger
            icon={<DeleteOutlined />}
            iconPosition="end"
            onClick={warningDelete}
          >
            {t("button.removeoperator")}
          </Button>
          <Flex gap={8}>
            <Button onClick={handleBack}>{t("buttons.cancel")}</Button>
            <Button
              type="primary"
              icon={<CheckOutlined />} iconPosition="end"
              onClick={handleSavePermissions}
            >
              {t("button.save")}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Spin>
  );
};
